import { ReactNode } from 'react';

import { SELECTED_SURFACE } from '@/components/ui/constants';
import { cn } from '@/lib/utils';

interface ResourceTileProps {
  icon: ReactNode;
  title: string;
  /** Shown beside the title, e.g. a `ResourceTileStatus`. */
  status?: ReactNode;
  /** The muted line under the title, e.g. a `ResourceTileMeta`. */
  meta?: ReactNode;
  toggle?: ReactNode;
  /** Opens the item. Without it the tile is static. */
  onOpen?: () => void;
  /** The item open in the side panel. */
  isSelected?: boolean;
}

/**
 * One item in a developer-settings list, in the Connections and Rules tile layout. When the
 * tile opens something, the title is a button stretched over the whole tile, so the switch stays
 * a separate control instead of a button nested in a button.
 */
export const ResourceTile = ({
  icon,
  title,
  status,
  meta,
  toggle,
  onOpen,
  isSelected,
}: ResourceTileProps) => (
  <li
    className={cn(
      'relative flex w-full items-center gap-4 rounded-lg border border-border bg-background px-4 py-4 transition-colors',
      onOpen &&
        'hover:bg-muted/50 has-[[data-tile-open]:focus-visible]:ring-2 has-[[data-tile-open]:focus-visible]:ring-ring has-[[data-tile-open]:focus-visible]:ring-offset-2',
      isSelected && cn(SELECTED_SURFACE, 'hover:bg-selected-soft')
    )}
  >
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border-[0.5px] border-border bg-subtle p-2">
      <div className="flex items-center justify-center rounded bg-background p-1 shadow-sm">
        {icon}
      </div>
    </div>

    <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
      <div className="flex min-w-0 items-center gap-3">
        {onOpen ? (
          <button
            type="button"
            data-tile-open
            onClick={onOpen}
            aria-current={isSelected || undefined}
            className="min-w-0 cursor-pointer truncate text-left text-base leading-none font-medium text-foreground after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none"
          >
            {title}
          </button>
        ) : (
          <span className="min-w-0 truncate text-base leading-none font-medium text-foreground">
            {title}
          </span>
        )}
        {status}
      </div>
      <div className="pointer-events-none">{meta}</div>
    </div>

    {/* Above the stretched open button, so the switch gets its own clicks. */}
    {toggle && <div className="relative z-10 flex shrink-0 items-center">{toggle}</div>}
  </li>
);
